import { type AppAbility, defineAbilitiesFor, type Role } from '@account-manager/shared';
import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import { useSession } from './client.js';

export interface Membership {
  organization_id: string;
  role: Role;
  organization_name: string;
  organization_slug: string;
}

interface AuthContextValue {
  loading: boolean;
  user: { id: string; email: string; name: string | null } | null;
  activeOrgId: string | null;
  activeRole: Role | null;
  activeOrgFiscalConfigComplete: boolean | null;
  memberships: Membership[];
  ability: AppAbility;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const session = useSession();
  const value: AuthContextValue = useMemo(() => {
    const data = (session.data ?? null) as Record<string, unknown> | null;
    const userRaw = data?.user as { id: string; email: string; name?: string | null } | undefined;
    const user = userRaw
      ? { id: userRaw.id, email: userRaw.email, name: userRaw.name ?? null }
      : null;
    const activeOrgId = (data?.activeOrgId as string | null | undefined) ?? null;
    const activeRole = (data?.activeRole as Role | null | undefined) ?? null;
    const activeOrgFiscalConfigComplete =
      (data?.activeOrgFiscalConfigComplete as boolean | null | undefined) ?? null;
    const memberships = (data?.memberships as Membership[] | undefined) ?? [];
    const ability = defineAbilitiesFor(
      { id: user?.id ?? '', role: activeRole, orgId: activeOrgId },
      activeOrgId ? { id: activeOrgId } : null,
    );
    return {
      loading: session.isPending,
      user,
      activeOrgId,
      activeRole,
      activeOrgFiscalConfigComplete,
      memberships,
      ability,
    };
  }, [session.data, session.isPending]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
